import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import {
  CategoriaBugReport,
  listarCategorias,
  deletarCategoria,
} from '../../services/bugReportService';
import { CategoriaModal } from './CategoriaModal';
import PermissionGate from '../PermissionGate';

export const CategoriasTab: React.FC = () => {
  const [categorias, setCategorias] = useState<CategoriaBugReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState<CategoriaBugReport | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const cats = await listarCategorias(false); // inclui inativas
      setCategorias(cats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleNova = () => {
    setSelectedCategoria(null);
    setModalOpen(true);
  };

  const handleEditar = (cat: CategoriaBugReport) => {
    setSelectedCategoria(cat);
    setModalOpen(true);
  };

  const handleDesativar = async (cat: CategoriaBugReport) => {
    if (!confirm(`Desativar categoria "${cat.nome}"?\n\nCategorias desativadas nao aparecem no dropdown de novas ocorrencias.`)) return;
    try {
      await deletarCategoria(cat.id);
      reload();
    } catch (err: any) {
      alert('Erro: ' + (err?.response?.data?.detail || err?.message));
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Categorias de Ocorrencias</h2>
          <p className="text-sm text-gray-600">
            Categorias sao sincronizadas para o app offline. Apenas MASTER pode editar.
          </p>
        </div>
        <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
          <button
            type="button"
            onClick={handleNova}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Nova Categoria
          </button>
        </PermissionGate>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : categorias.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhuma categoria cadastrada</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Ordem</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Nome</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Descricao</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Ativo</th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categorias.map((cat) => (
                <tr key={cat.id} className={!cat.ativo ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-4 py-3 text-sm text-gray-500">{cat.ordem}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{cat.nome}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-md truncate">{cat.descricao || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {cat.ativo ? (
                      <Check className="w-5 h-5 text-green-600 inline" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 inline" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                        <button
                          type="button"
                          onClick={() => handleEditar(cat)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </PermissionGate>
                      {cat.ativo && (
                        <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
                          <button
                            type="button"
                            onClick={() => handleDesativar(cat)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Desativar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <CategoriaModal
          categoria={selectedCategoria}
          onClose={() => setModalOpen(false)}
          onSaved={reload}
        />
      )}
    </div>
  );
};
